// Client-side search over the local IndexedDB email cache.
// 100% local: no Firestore round-trips, no provider calls, no backend.
//
// v1 is a plain lowercased substring index grouped per thread. Substring
// matching handles docket/patent/serial numbers uniformly ("ZL0025-0008",
// "PCTUS2020012317", partial numbers) where a word-tokenizing full-text
// library would split on dashes/dots; per the plan this is the acceptable v1
// before considering MiniSearch/FlexSearch.

import * as emailCache from './email_cache.js';

const MAX_BODY_CHARS = 20000;    // per-message body contribution to the index
const MAX_THREAD_CHARS = 100000; // cap on a thread's total indexed text

let threadIndex = null; // Map<threadId, {threadId, haystack, rawMessages, newestDate}>
let indexAccount = null; // account the current index was built for
let dirty = true;

function markDirty() {
  dirty = true;
}

function stripTags(html) {
  return (html || '').replace(/<[^>]*>/g, ' ');
}

// Extracts searchable text from a raw provider message without DOMParser
// (fast enough to index tens of thousands of messages).
function messageText(row) {
  let body = '';
  const raw = row.raw || {};
  if (raw.body && raw.body.content) {
    // Graph message
    body = raw.body.content;
  } else if (raw.snippet !== undefined || (raw.payload && raw.payload.headers)) {
    // Gmail message: body is base64 in payload parts; reuse the provider-agnostic
    // fields we normalized at sync time plus a tag-stripped body if present
    body = gmailBodyText(raw);
  }
  return `${row.subject || ''} ${row.fromAddress || ''} ${row.attachmentText || ''} ${stripTags(body).slice(0, MAX_BODY_CHARS)}`;
}

function gmailBodyText(payload) {
  // mirrors gmail.js extractGmailBody but tag-stripped and length-capped,
  // duplicated here to keep indexing provider-agnostic and fast
  let text = '';
  function traverse(part) {
    if (!part || text.length >= MAX_BODY_CHARS) return;
    const mimeType = part.mimeType;
    const body = part.body || {};
    if ((mimeType === 'text/plain' || mimeType === 'text/html') && body.data) {
      text += ' ' + decodeBase64(body.data);
    } else if (mimeType && mimeType.startsWith('multipart/') && part.parts) {
      for (const subPart of part.parts) {
        traverse(subPart);
      }
    }
  }
  traverse(payload.payload || payload);
  return text;
}

function decodeBase64(encodedString) {
  if (!encodedString) {
    return '';
  }
  try {
    return decodeURIComponent(
      atob(encodedString.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch (e) {
    return '';
  }
}

async function buildIndex(account) {
  const rows = await emailCache.getAllMessages(account);
  const map = new Map();
  for (const row of rows) {
    if (!row.threadId) {
      continue;
    }
    let t = map.get(row.threadId);
    if (!t) {
      t = { threadId: row.threadId, haystack: '', rawMessages: [], newestDate: 0 };
      map.set(row.threadId, t);
    }
    // rows arrive date-ascending, so rawMessages end up chronological
    t.rawMessages.push(row.raw);
    if (t.haystack.length < MAX_THREAD_CHARS) {
      t.haystack += ' ' + messageText(row);
      if (t.haystack.length > MAX_THREAD_CHARS) {
        t.haystack = t.haystack.slice(0, MAX_THREAD_CHARS);
      }
    }
    if ((row.date || 0) >= t.newestDate) {
      t.newestDate = row.date || 0;
    }
  }
  for (const t of map.values()) {
    t.haystack = t.haystack.toLowerCase();
  }
  threadIndex = map;
  indexAccount = account;
  dirty = false;
}

// Returns matching threads, newest first: [{threadId, rawMessages, newestDate}]
// The index is scoped to one sync account; switching accounts rebuilds it.
async function searchThreads(query, limit = 100, account = null) {
  if (!query || !query.trim() || !account) {
    return [];
  }
  if (dirty || !threadIndex || indexAccount !== account) {
    await buildIndex(account);
  }
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const matches = [];
  for (const t of threadIndex.values()) {
    let ok = true;
    for (const term of terms) {
      if (!t.haystack.includes(term)) {
        ok = false;
        break;
      }
    }
    if (ok) {
      matches.push(t);
    }
  }
  matches.sort((a, b) => b.newestDate - a.newestDate);
  return matches.slice(0, limit);
}

export {
  markDirty,
  searchThreads,
};
